import { useNavigation, useRoute } from '@react-navigation/native'
import { debounce } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { Keyboard, Platform } from 'react-native'
import styled from 'styled-components/native'

import { useAuthContext } from 'features/auth/context/AuthContext'
import { AddressOption } from 'features/identityCheck/components/AddressOption'
import { ProfileTypes } from 'features/identityCheck/pages/profile/enums'
import { IdentityCheckError } from 'features/identityCheck/pages/profile/errors'
import { addressActions, useAddress } from 'features/identityCheck/pages/profile/store/addressStore'
import { useCity } from 'features/identityCheck/pages/profile/store/cityStore'
import { shouldProvidePhoneNumber } from 'features/identityCheck/pages/profile/utils'
import { UseNavigationType, UseRouteType } from 'features/navigation/navigators/RootNavigator/types'
import { getSubscriptionHookConfig } from 'features/navigation/navigators/SubscriptionStackNavigator/getSubscriptionHookConfig'
import { AccessibilityRole } from 'libs/accessibilityRole/accessibilityRole'
import { useFeatureFlag } from 'libs/firebase/firestore/featureFlags/useFeatureFlag'
import { RemoteStoreFeatureFlags } from 'libs/firebase/firestore/types'
import { eventMonitoring } from 'libs/monitoring/services'
import { useAddressesQuery } from 'libs/place/queries/useAddressesQuery'
import { useIdCheckAddressAutocompletion } from 'queries/settings/useSettings'
import { Form } from 'ui/components/Form'
import { isAddressValid } from 'ui/components/inputs/addressCheck'
import { InputError } from 'ui/components/inputs/InputError'
import { Spinner } from 'ui/components/Spinner'
import { Button } from 'ui/designSystem/Button/Button'
import { SearchInput } from 'ui/designSystem/SearchInput/SearchInput'
import { showErrorSnackBar } from 'ui/designSystem/Snackbar/snackBar.store'
import { useEnterKeyAction } from 'ui/hooks/useEnterKeyAction'
import { PageWithHeader } from 'ui/pages/PageWithHeader'
import { Typo } from 'ui/theme'
import { getHeadingAttrs } from 'ui/theme/typographyAttrs/getHeadingAttrs'

const snackbarMessage =
  'Nous avons eu un problème pour trouver l’adresse associée à ton code postal. Réessaie plus tard.'
const exception = 'Failed to fetch data from API: https://data.geopf.fr/geocodage/search'

export const SetAddress = () => {
  const { params } = useRoute<UseRouteType<'SetAddress'>>()
  const type = params?.type ?? ProfileTypes.IDENTITY_CHECK // Fallback to most common scenario
  const origin = params?.origin
  const freeOfferId = params?.freeOfferId

  const { user } = useAuthContext()
  const identityCheckAndRecapExistingDataConfig = {
    headerTitle: 'Profil',
    title: 'Quelle est ton adresse\u00a0?',
  }
  const pageConfigByType = {
    [ProfileTypes.IDENTITY_CHECK]: identityCheckAndRecapExistingDataConfig,
    [ProfileTypes.BOOKING_FREE_OFFER_15_16]: {
      headerTitle: 'Informations personnelles',
      title: 'Saisis ton adresse postale',
    },
    [ProfileTypes.RECAP_EXISTING_DATA]: identityCheckAndRecapExistingDataConfig,
  }

  const { data: idCheckAddressAutocompletion } = useIdCheckAddressAutocompletion()
  const phoneNumberInProfileStepper = useFeatureFlag(
    RemoteStoreFeatureFlags.WIP_PHONE_NUMBER_IN_PROFILE_STEPPER
  )
  const storedAddress = useAddress()
  const defaultAddress = user?.street ?? storedAddress
  const storedCity = useCity()
  const { setAddress: setStoreAddress } = addressActions
  const { navigate } = useNavigation<UseNavigationType>()
  const [query, setQuery] = useState<string>(defaultAddress ?? '')
  const [debouncedQuery, setDebouncedQuery] = useState<string>(query)
  const [selectedAddress, setSelectedAddress] = useState<string | null>(defaultAddress ?? null)
  const debouncedSetQuery = useRef(debounce(setDebouncedQuery, 500)).current
  const {
    data: addresses = [],
    isLoading,
    isError,
  } = useAddressesQuery({
    query: debouncedQuery,
    cityCode: storedCity?.code ?? '',
    postalCode: storedCity?.postalCode ?? '',
    enabled: !!idCheckAddressAutocompletion && debouncedQuery.length > 0,
    limit: 10,
  })

  useEffect(() => {
    if (!isError) return
    showErrorSnackBar(snackbarMessage)
    eventMonitoring.captureException(new IdentityCheckError(exception))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError])

  const onChangeAddress = (value: string) => {
    setSelectedAddress(null)
    setQuery(value)
    debouncedSetQuery(value)
  }

  const onAddressSelection = (address: string) => {
    setSelectedAddress(address)
    setQuery(address)
    debouncedSetQuery(address)
    Keyboard.dismiss()
  }

  const resetSearch = () => {
    setSelectedAddress(null)
    setQuery('')
    debouncedSetQuery('')
  }

  const isValidAddress = selectedAddress !== null || isAddressValid(query)

  const hasError = !isValidAddress && query.length > 0

  const enabled = query.trim().length > 0 && !hasError

  const label = idCheckAddressAutocompletion
    ? 'Recherche et sélectionne ton adresse'
    : 'Entre ton adresse'

  const getStatusNavigationParams = () => {
    if (origin) {
      return freeOfferId ? { type, origin, freeOfferId } : { type, origin }
    }
    return freeOfferId ? { type, freeOfferId } : { type }
  }

  const submitAddress = async () => {
    if (!enabled) return
    setStoreAddress(selectedAddress ?? query)
    if (phoneNumberInProfileStepper && shouldProvidePhoneNumber(user)) {
      navigate(...getSubscriptionHookConfig('SetPhoneNumber', { type }))
    } else {
      navigate(...getSubscriptionHookConfig('SetStatus', getStatusNavigationParams()))
    }
  }

  const accessibilityLabel =
    phoneNumberInProfileStepper && shouldProvidePhoneNumber(user)
      ? 'Continuer vers le numéro de téléphone'
      : 'Continuer vers le statut'
  const errorMessage =
    'Ton adresse ne doit pas contenir de caractères spéciaux ou n’être composée que d’espaces.'

  useEnterKeyAction(enabled ? submitAddress : undefined)

  return (
    <PageWithHeader
      shouldLimitWidth
      title={pageConfigByType[type].headerTitle}
      scrollChildren={
        <React.Fragment>
          <Form.MaxWidth>
            <Typo.Title3 {...getHeadingAttrs(2)}>{pageConfigByType[type].title}</Typo.Title3>
            <Container>
              <SearchInput
                onChangeText={onChangeAddress}
                value={query}
                label={label}
                description="Exemple&nbsp;: 34 avenue de l’Opéra"
                autoComplete="street-address"
                accessibilityHint={errorMessage}
                onClear={resetSearch}
                returnKeyType="next"
                testID="Entrée pour l’adresse"
              />
              <InputError visible={hasError} errorMessage={errorMessage} numberOfSpacesTop={2} />
            </Container>
          </Form.MaxWidth>
          {isLoading ? <Spinner /> : null}
          <AdressesContainer accessibilityRole={AccessibilityRole.RADIOGROUP}>
            {addresses.map((address, index) => (
              <AddressOption
                label={address}
                selected={address === selectedAddress}
                onPressOption={onAddressSelection}
                optionKey={address}
                key={address}
                accessibilityLabel={`Proposition d’adresse ${index + 1}\u00a0: ${address}`}
              />
            ))}
          </AdressesContainer>
        </React.Fragment>
      }
      fixedBottomChildren={
        <Button
          type="submit"
          onPress={submitAddress}
          wording="Continuer"
          accessibilityLabel={accessibilityLabel}
          disabled={!enabled}
        />
      }
    />
  )
}

const AdressesContainer = styled.View({
  flexGrow: 1,
  overflowY: 'scroll',
  ...(Platform.OS === 'web' ? { boxSizing: 'content-box' } : {}),
})

const Container = styled.View(({ theme }) => ({
  marginTop: theme.designSystem.size.spacing.xl,
  marginBottom: theme.designSystem.size.spacing.s,
}))
